/* eslint-disable complexity */
import { useState, useEffect, useCallback, useRef } from "react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import FeatureGates from "@atlaskit/feature-gate-js-client";
import Modal, { ModalTransition } from "@atlaskit/modal-dialog";
import { JiraBackground } from "@atlassian/product-wireframes";

import {
    UfoCustomTimingKey,
    ufoEventOnboardingWizardProjectCreation,
    ufoEventOnboardingWizardProjectCreationIncludingProvisioning
} from "@/components/signup/utils";
import {
    useBxpWebStargateClient,
    WizardCreateProjectPayload
} from "@/libraries/bxp-web-stargate-client/src";
import {
    useCloudStatus,
    useCloudStatusRedirection,
    useSearchParameters
} from "@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks";
import {
    experiments,
    getExperiment,
    getExperimentWithExposures
} from "@/utils/experiment";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

import { getProductQueryParamTeamType } from "../../../models/user-segmentation";
import { ANALYTICS_PREFIX } from "../common/constants";
import {
    useShowOnboardingWizardScreenForKanban,
    useShowOnboardingWizardScreenForRouteBiz,
    useEligibleForSimplifiedLandingViewWithBasicTasks,
    useWizardConfiguration
} from "../controllers";
import { ProjectConfig } from "../utils/types";

import { WelcomeModalPageSwitcher } from "./page-switcher/page-switcher";

export const WelcomeModal = ({
    onComplete,
    withSampleData
}: {
    onComplete: (taskId?: string, onRedirect?: () => void) => void;
    withSampleData: boolean;
}) => {
    const [projectConfig, setProjectConfig] = useState<ProjectConfig | null>(
        null
    );
    const isCreateRequestInProgress = useRef(false);
    const { userEligibleForWizardForRouteBizExperiment } =
        useShowOnboardingWizardScreenForRouteBiz();
    const { userEligibleForWizardForDevKanbanExperiment } =
        useShowOnboardingWizardScreenForKanban();
    const { userEligibleForSimplifiedLandingView } =
        useEligibleForSimplifiedLandingViewWithBasicTasks();

    const { createAnalyticsEvent } = useAnalyticsEvents();

    const { createCloudStatus, cloudId } = useCloudStatus();
    const { deferRedirectView, setDeferRedirectView } =
        useCloudStatusRedirection();

    const { signupId } = useSearchParameters();

    const stargateClient = useBxpWebStargateClient();

    const { teamType, projectDetails } = useWizardConfiguration();

    useEffect(() => {
        createAnalyticsEvent({
            eventType: "screen",
            name: `${ANALYTICS_PREFIX}Screen`,
            attributes: {
                teamType: teamType,
                template: projectDetails.template
            }
        }).fire();
    }, [createAnalyticsEvent, projectDetails.template, teamType]);

    // Prevent use-cloud-status.tsx from immediately redirecting when provisioning is finished.
    useEffect(() => {
        if (!deferRedirectView) {
            if (setDeferRedirectView) {
                setDeferRedirectView("jira-onboarding-wizard");
            }
        }
    });

    const handleComplete = (config: ProjectConfig) => {
        setProjectConfig(config);
        ufoEventOnboardingWizardProjectCreationIncludingProvisioning.start();

        if (checkFeatureFlagGate(featureFlags.jiraAddSignupIdToEvents)) {
            ufoEventOnboardingWizardProjectCreationIncludingProvisioning.setCustomData(
                {
                    signupId
                }
            );
        }
    };

    const isSimplifiedLandingViewWithBasicTasks = useCallback(() => {
        if (!userEligibleForSimplifiedLandingView) {
            return false;
        }

        const simplifiedListViewWithBasicTasksExperimentConfig =
            getExperimentWithExposures(
                experiments.jfindSimplifiedListViewWithBasicTasks
            );

        return (
            simplifiedListViewWithBasicTasksExperimentConfig.type ===
                "enrolled" &&
            simplifiedListViewWithBasicTasksExperimentConfig.value
        );
    }, [userEligibleForSimplifiedLandingView]);

    const isWizardRouteBizExperiment = useCallback(() => {
        const wizardRouteBizExperimentConfig = getExperiment(
            experiments.jiraProjectOnboardingWizardForRouteBiz
        );

        return (
            userEligibleForWizardForRouteBizExperiment &&
            wizardRouteBizExperimentConfig.type === "enrolled" &&
            wizardRouteBizExperimentConfig.value
        );
    }, [userEligibleForWizardForRouteBizExperiment]);

    const isWizardDevKanbanExperiment = useCallback(() => {
        const wizardDevKanbanExperimentConfig = getExperiment(
            experiments.jiraProjectOnboardingWizardDevKanban
        );

        return (
            userEligibleForWizardForDevKanbanExperiment &&
            wizardDevKanbanExperimentConfig.type === "enrolled" &&
            wizardDevKanbanExperimentConfig.value
        );
    }, [userEligibleForWizardForDevKanbanExperiment]);

    const isAllowExtraFieldsExperiment = useCallback(() => {
        const allowExtraFieldsExperimentConfig = getExperimentWithExposures(
            experiments.jiraProjectOnboardingExtraFieldsForWizards
        );

        return (
            allowExtraFieldsExperimentConfig.type === "enrolled" &&
            allowExtraFieldsExperimentConfig.value
        );
    }, []);

    const createProject = useCallback(async () => {
        if (projectConfig && cloudId) {
            ufoEventOnboardingWizardProjectCreation.start();

            if (checkFeatureFlagGate(featureFlags.jiraAddSignupIdToEvents)) {
                ufoEventOnboardingWizardProjectCreation.setCustomData({
                    signupId
                });
            }

            ufoEventOnboardingWizardProjectCreationIncludingProvisioning.startSubstep(
                UfoCustomTimingKey.ONBOARDING_WIZARD_PROJECT_CREATE_API_CALL
            );

            try {
                isCreateRequestInProgress.current = true;

                const isWizardDevKanbanExperimentEnabled =
                    isWizardDevKanbanExperiment();

                const isWizardRouteBizExperimentEnabled =
                    isWizardRouteBizExperiment();

                const isAllowExtraFieldsExperimentEnabled =
                    isAllowExtraFieldsExperiment();

                const isSimplifiedLandingViewWithBasicTasksEnabled =
                    isSimplifiedLandingViewWithBasicTasks();

                const projectPayload: WizardCreateProjectPayload = {
                    projectName: projectConfig.projectName,
                    issueTypes: projectConfig.issueTypes,
                    statusNames: projectConfig.columns,
                    projectKey: projectDetails.projectKey,
                    template: projectDetails.template,
                    cloudId: cloudId,
                    teamType: getProductQueryParamTeamType(teamType),
                    ...(isWizardDevKanbanExperimentEnabled ||
                    isSimplifiedLandingViewWithBasicTasksEnabled ||
                    FeatureGates.checkGate(
                        "jira_onboarding_wizard_small_refactor"
                    )
                        ? {
                              isSampleDataIncluded:
                                  isWizardDevKanbanExperimentEnabled ||
                                  isSimplifiedLandingViewWithBasicTasksEnabled ||
                                  withSampleData
                          }
                        : {}),
                    ...(isAllowExtraFieldsExperimentEnabled ||
                    isWizardRouteBizExperimentEnabled ||
                    isSimplifiedLandingViewWithBasicTasksEnabled ||
                    FeatureGates.checkGate(
                        "jira_onboarding_wizard_small_refactor"
                    )
                        ? {
                              customDetail: {
                                  ...(isAllowExtraFieldsExperimentEnabled ||
                                  isWizardRouteBizExperimentEnabled
                                      ? { enableExtraFields: true }
                                      : {}),
                                  ...(isSimplifiedLandingViewWithBasicTasksEnabled
                                      ? { sampleDataVersion: "GENERIC" }
                                      : {}),
                                  ...(withSampleData
                                      ? { sampleDataVersion: "WIZARD" }
                                      : {})
                              }
                          }
                        : {})
                };
                const { taskId } = await stargateClient.wizardProjectCreate(
                    projectPayload
                );

                ufoEventOnboardingWizardProjectCreation.success({
                    customData: {
                        template: projectDetails.template
                    }
                });

                if (
                    checkFeatureFlagGate(
                        featureFlags.jiraOnboardingWizardMetricImprovements
                    )
                ) {
                    const onRedirect = () => {
                        ufoEventOnboardingWizardProjectCreationIncludingProvisioning.success(
                            {
                                customData: {
                                    template: projectDetails.template
                                }
                            }
                        );
                    };

                    onComplete(taskId, onRedirect);
                } else {
                    ufoEventOnboardingWizardProjectCreationIncludingProvisioning.success(
                        {
                            customData: {
                                template: projectDetails.template
                            }
                        }
                    );
                    onComplete(taskId);
                }
            } catch (error) {
                ufoEventOnboardingWizardProjectCreation.failure({
                    customData: {
                        template: projectDetails.template
                    },
                    error
                });

                if (
                    checkFeatureFlagGate(
                        featureFlags.jiraOnboardingWizardMetricImprovements
                    )
                ) {
                    const onRedirect = () => {
                        ufoEventOnboardingWizardProjectCreationIncludingProvisioning.failure(
                            {
                                customData: {
                                    template: projectDetails.template
                                },
                                error
                            }
                        );
                    };

                    onComplete(undefined, onRedirect);
                } else {
                    ufoEventOnboardingWizardProjectCreationIncludingProvisioning.failure(
                        {
                            customData: {
                                template: projectDetails.template
                            },
                            error
                        }
                    );
                    onComplete();
                }
            } finally {
                if (
                    checkFeatureFlagGate(
                        featureFlags.jiraOnboardingMultipleProjectRequestsBugfix
                    )
                ) {
                    // skip updating isCreateRequestInProgress ref.
                    return;
                }

                isCreateRequestInProgress.current = false;
            }
        }
    }, [
        projectConfig,
        cloudId,
        signupId,
        isWizardDevKanbanExperiment,
        isWizardRouteBizExperiment,
        isAllowExtraFieldsExperiment,
        isSimplifiedLandingViewWithBasicTasks,
        projectDetails.projectKey,
        projectDetails.template,
        teamType,
        withSampleData,
        stargateClient,
        onComplete
    ]);

    useEffect(() => {
        // Only trigger creating project once the site is ready
        const canCreateProject =
            projectConfig && createCloudStatus === "success" && cloudId;

        if (canCreateProject && !isCreateRequestInProgress.current) {
            createProject();
        }
    }, [createCloudStatus, cloudId, createProject, projectConfig]);

    useEffect(() => {
        if (createCloudStatus === "success") {
            createAnalyticsEvent({
                eventType: "ui",
                action: "succeeded",
                actionSubject: "provisioning",
                actionSubjectId: "jiraAnimatedProvisioningScreen",
                source: "/ondemand/signup/confirmation",
                attributes: {
                    atlasId: "ATLAS-56326",
                    reactComponent: "WelcomeModal",
                    template: projectDetails.template,
                    teamType: teamType
                }
            }).fire();
        }
    }, [
        createAnalyticsEvent,
        createCloudStatus,
        projectDetails.template,
        teamType
    ]);

    return (
        <>
            <JiraBackground />
            <ModalTransition>
                <Modal
                    shouldCloseOnOverlayClick={false}
                    shouldCloseOnEscapePress={false}
                    width="1140px"
                    height="626px"
                >
                    <WelcomeModalPageSwitcher onComplete={handleComplete} />
                </Modal>
            </ModalTransition>
        </>
    );
};
